/**
 * The "How to use the Norican IT HelpDesk" guide — the body of the built-in
 * help notice (`builtin:help`).
 *
 * It used to be Markdown tips rendered like any other notice. A guide read by
 * employees in fifteen countries on launch day needs to be seen before it is
 * read, so this is a small page of DS cards instead: three illustrated steps,
 * incident versus request, the request catalogue in plain words, what happens
 * next, tips, questions and — last and deliberately low-key — other ways to
 * reach IT. Every word comes from the `assistant` catalogue (`help.*`), and the
 * status names are the ticket pages' own labels, so the guide can never
 * describe a status the app does not show.
 *
 * No phone number and no emergency route here, by decision: the site offers
 * no phone number anywhere (20 Oct).
 */

import {
  AppWindow,
  ArrowRight,
  Briefcase,
  Camera,
  CircleCheck,
  ClipboardList,
  Database,
  Icon,
  type IconComponent,
  Laptop,
  Layers,
  ListChecks,
  Mail,
  MessageSquare,
  Package,
  Paperclip,
  RefreshCw,
  Ticket,
  UserRoundPlus,
  Users,
  Wrench,
} from "@norican/icons";
import { Accordion, Badge, Button, Card, Typography } from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useT } from "@/i18n";
import { HELPDESK_EMAIL, HELPDESK_EMAIL_HREF, HELPDESK_TEAMS_URL } from "@/lib/helpdesk-contact";

/** Request catalogue groups, in the order Rusty asked for (people last). */
const CATALOG = [
  { key: "software", icon: AppWindow },
  { key: "hardware", icon: Laptop },
  { key: "access", icon: Users },
  { key: "business", icon: Briefcase },
  { key: "data", icon: Database },
  { key: "people", icon: UserRoundPlus },
] as const;

const TIPS = [
  { key: "screenshot", icon: Camera },
  { key: "changed", icon: RefreshCw },
  { key: "system", icon: Layers },
  { key: "one", icon: ListChecks },
] as const;

const FAQ = ["email", "old", "colleague", "starter", "language", "privacy", "feedback"] as const;

type BadgeTone = "neutral" | "accent" | "success" | "warning";

/** Rows of the status table: which app labels share one meaning. */
const STATUSES = [
  { meaning: "received", tone: "neutral", labels: ["status.LOGGED", "requestStatus.LOGGED"] },
  { meaning: "active", tone: "accent", labels: ["status.ACTIVE"] },
  { meaning: "waitingYou", tone: "warning", labels: ["status.WAITING_CUSTOMER"] },
  {
    meaning: "supplier",
    tone: "neutral",
    labels: ["status.WAITING_THIRD_PARTY", "requestStatus.WAITING_THIRD_PARTY"],
  },
  {
    meaning: "done",
    tone: "success",
    labels: ["status.RESOLVED", "requestStatus.RESOLVED", "requestStatus.FULFILLED"],
  },
  { meaning: "closed", tone: "neutral", labels: ["status.CLOSED", "status.CANCELLED"] },
] as const satisfies readonly { meaning: string; tone: BadgeTone; labels: readonly string[] }[];

export function HelpGuide() {
  const { t } = useT("assistant");
  const { t: tt } = useT("tickets");

  return (
    <div className="flex flex-col gap-10">
      <Typography as="p" variant="body-lg" className="text-pretty">
        {t("help.subtitle")}
      </Typography>

      <Section title={t("help.steps.title")}>
        <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-stretch">
          <Step number={1} icon={MessageSquare} title={t("help.steps.one.title")}>
            <div className="bg-accent text-fg-on-inverse self-start rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
              {t("help.steps.one.example")}
            </div>
            <Caption>{t("help.steps.one.caption")}</Caption>
          </Step>
          <StepArrow />
          <Step number={2} icon={ClipboardList} title={t("help.steps.two.title")}>
            <div className="bg-bg-subtle border-border flex flex-col gap-1.5 rounded-lg border p-3 text-left text-sm">
              <FormLine done label={t("help.steps.two.fieldA")} />
              <FormLine done label={t("help.steps.two.fieldB")} />
              <FormLine label={t("help.steps.two.fieldC")} />
              <FormLine icon={Paperclip} label={t("help.steps.two.fieldD")} />
              <span className="bg-accent text-fg-on-inverse mt-1 self-end rounded-md px-3 py-0.5 text-xs font-semibold">
                {t("help.steps.two.submit")}
              </span>
            </div>
            <Caption>{t("help.steps.two.caption")}</Caption>
          </Step>
          <StepArrow />
          <Step number={3} icon={Ticket} title={t("help.steps.three.title")}>
            <div className="flex flex-col gap-1.5">
              <TicketLine
                reference={tt("reference.id.INCIDENT", { number: "447" })}
                status={tt("status.ACTIVE")}
                tone="accent"
              />
              <TicketLine
                reference={tt("reference.id.SERVICE_REQUEST", { number: "10449" })}
                status={tt("requestStatus.LOGGED")}
                tone="neutral"
              />
            </div>
            <Caption>{t("help.steps.three.caption")}</Caption>
          </Step>
        </div>
        <Typography as="p" variant="body-sm" tone="muted" className="mt-3 text-center">
          {t("help.steps.note")}
        </Typography>
      </Section>

      <Section title={t("help.kinds.title")} intro={t("help.kinds.intro")}>
        <div className="grid gap-3 md:grid-cols-2">
          <KindCard
            kind="incident"
            icon={Wrench}
            tone="danger"
            tag={t("help.kinds.incident.tag")}
            title={t("help.kinds.incident.title")}
            body={t("help.kinds.incident.body")}
            examples={[
              t("help.kinds.incident.e1"),
              t("help.kinds.incident.e2"),
              t("help.kinds.incident.e3"),
              t("help.kinds.incident.e4"),
              t("help.kinds.incident.e5"),
            ]}
          />
          <KindCard
            kind="request"
            icon={Package}
            tone="accent"
            tag={t("help.kinds.request.tag")}
            title={t("help.kinds.request.title")}
            body={t("help.kinds.request.body")}
            examples={[
              t("help.kinds.request.e1"),
              t("help.kinds.request.e2"),
              t("help.kinds.request.e3"),
              t("help.kinds.request.e4"),
              t("help.kinds.request.e5"),
            ]}
          />
        </div>
      </Section>

      <Section title={t("help.catalog.title")} intro={t("help.catalog.intro")}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CATALOG.map(({ key, icon }) => (
            <Card key={key} variant="subtle" padding="md" className="flex flex-col gap-2">
              <span className="flex items-center gap-2">
                <Icon icon={icon} size="sm" tone="accent" />
                <Typography as="h3" variant="heading-6">
                  {t(`help.catalog.${key}.title`)}
                </Typography>
              </span>
              <Typography as="p" variant="body-sm" tone="muted">
                {t(`help.catalog.${key}.body`)}
              </Typography>
            </Card>
          ))}
        </div>
      </Section>

      <Section title={t("help.next.title")}>
        <ul className="flex flex-col gap-2">
          {(["number", "team", "waiting", "approval"] as const).map((key) => (
            <li key={key} className="flex gap-2">
              <Icon icon={CircleCheck} size="sm" tone="success" className="mt-1 shrink-0" />
              <Typography as="span" variant="body">
                {t(`help.next.${key}`)}
              </Typography>
            </li>
          ))}
        </ul>
        <Typography as="h3" variant="heading-6" className="mt-5 mb-2">
          {t("help.next.statusesTitle")}
        </Typography>
        <div className="border-border divide-border divide-y rounded-lg border">
          {STATUSES.map(({ meaning, tone, labels }) => (
            <div
              key={meaning}
              className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <span className="flex flex-wrap gap-1.5 sm:w-2/5">
                {[...new Set(labels.map((label) => tt(label)))].map((label) => (
                  <Badge key={label} tone={tone} variant="subtle" size="sm">
                    {label}
                  </Badge>
                ))}
              </span>
              <Typography as="span" variant="body-sm">
                {t(`help.next.${meaning}`)}
              </Typography>
            </div>
          ))}
        </div>
      </Section>

      <Section title={t("help.tips.title")}>
        <div className="grid gap-3 sm:grid-cols-2">
          {TIPS.map(({ key, icon }) => (
            <Card key={key} variant="outline" padding="md" className="flex gap-3">
              <Icon icon={icon} size="md" tone="accent" className="shrink-0" />
              <span className="flex flex-col gap-1">
                <Typography as="h3" variant="heading-6">
                  {t(`help.tips.${key}.title`)}
                </Typography>
                <Typography as="p" variant="body-sm" tone="muted">
                  {t(`help.tips.${key}.body`)}
                </Typography>
              </span>
            </Card>
          ))}
        </div>
      </Section>

      <Section title={t("help.faq.title")}>
        <Accordion.Root className="border-border rounded-lg border">
          {FAQ.map((key) => (
            <Accordion.Item key={key} value={key}>
              <Accordion.Header>
                <Accordion.Trigger>{t(`help.faq.${key}.q`)}</Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Panel>
                <Typography as="p" variant="body-sm" className="px-4 pb-4">
                  {t(`help.faq.${key}.a`)}
                </Typography>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion.Root>
      </Section>

      <Section title={t("help.other.title")}>
        <Card
          variant="outline"
          padding="lg"
          accent="left"
          accentTone="accent"
          className="flex flex-col gap-2"
        >
          <Typography as="h3" variant="heading-5">
            {t("help.other.fastest")}
          </Typography>
          <Typography as="p" variant="body">
            {t("help.other.fastestBody")}
          </Typography>
          <span>
            <Button render={<Link to="/ask" />} endIcon={<Icon icon={ArrowRight} size="sm" />}>
              {t("help.open")}
            </Button>
          </span>
        </Card>
        <Typography as="p" variant="body-sm" tone="muted" className="mt-4 mb-2">
          {t("help.other.alsoLead")}
        </Typography>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
          <a
            href={HELPDESK_TEAMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-fg-subtle inline-flex items-center gap-2 text-sm hover:underline"
          >
            <Icon icon={MessageSquare} size="sm" />
            {t("help.other.teams")}
          </a>
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <a
              href={HELPDESK_EMAIL_HREF}
              className="text-accent-fg-subtle inline-flex items-center gap-2 hover:underline"
            >
              <Icon icon={Mail} size="sm" />
              {HELPDESK_EMAIL}
            </a>
            <span className="text-fg-muted">{t("help.other.emailNote")}</span>
          </span>
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <Typography as="h2" variant="heading-4">
        {title}
      </Typography>
      {intro === undefined ? null : (
        <Typography as="p" variant="body" tone="muted" className="text-pretty">
          {intro}
        </Typography>
      )}
      <div>{children}</div>
    </section>
  );
}

function Step({
  number,
  icon,
  title,
  children,
}: {
  number: number;
  icon: IconComponent;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card variant="outline" padding="md" className="flex flex-col items-center gap-3 text-center">
      <span className="bg-accent-subtle text-accent-fg-subtle flex size-14 items-center justify-center rounded-full">
        <Icon icon={icon} size="lg" />
      </span>
      <span className="flex items-center gap-2">
        <span className="bg-accent text-fg-on-inverse flex size-6 items-center justify-center rounded-full text-xs font-bold">
          {number}
        </span>
        <Typography as="h3" variant="heading-5">
          {title}
        </Typography>
      </span>
      <div className="flex w-full flex-col items-stretch gap-3">{children}</div>
    </Card>
  );
}

function StepArrow() {
  return (
    <span aria-hidden className="text-fg-subtle flex items-center justify-center py-1 md:py-0">
      <Icon icon={ArrowRight} size="md" className="rotate-90 md:rotate-0" />
    </span>
  );
}

function Caption({ children }: { children: ReactNode }) {
  return (
    <Typography as="p" variant="body-sm" tone="muted" className="text-pretty">
      {children}
    </Typography>
  );
}

function FormLine({
  label,
  done = false,
  icon,
}: {
  label: string;
  done?: boolean;
  icon?: IconComponent;
}) {
  return (
    <span className="flex items-center gap-2">
      <Icon
        icon={icon ?? CircleCheck}
        size="xs"
        className={done ? "text-success-fg" : "text-fg-subtle"}
      />
      <span className={done ? "text-fg" : "text-fg-muted"}>{label}</span>
    </span>
  );
}

function TicketLine({
  reference,
  status,
  tone,
}: {
  reference: string;
  status: string;
  tone: BadgeTone;
}) {
  return (
    <span className="bg-bg-subtle border-border flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-xs">
      <span className="font-mono font-semibold whitespace-nowrap">{reference}</span>
      <Badge tone={tone} variant="subtle" size="sm">
        {status}
      </Badge>
    </span>
  );
}

function KindCard({
  icon,
  tone,
  tag,
  title,
  body,
  examples,
}: {
  kind: "incident" | "request";
  icon: IconComponent;
  tone: "danger" | "accent";
  tag: string;
  title: string;
  body: string;
  examples: string[];
}) {
  return (
    <Card
      variant="outline"
      padding="md"
      accent="left"
      accentTone={tone}
      className="flex flex-col gap-2"
    >
      <span className="flex items-center gap-2">
        <Icon icon={icon} size="sm" tone={tone} />
        <Badge tone={tone} variant="subtle" size="sm">
          {tag}
        </Badge>
      </span>
      <Typography as="h3" variant="heading-5">
        {title}
      </Typography>
      <Typography as="p" variant="body-sm" tone="muted">
        {body}
      </Typography>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {examples.map((example) => (
          <li key={example}>
            <Typography as="span" variant="body-sm">
              {example}
            </Typography>
          </li>
        ))}
      </ul>
    </Card>
  );
}
